import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Skeleton } from 'primereact/skeleton';
import { InputText } from 'primereact/inputtext';
import { Calendar } from 'primereact/calendar';
import { useAuth } from '../../context/AuthContext';
import CustomerSelection from '../../components/Common/CustomerSelection';
import { GetMyOrderList, GetMyOrderDetail } from "../../api/styleJewelleryService";
import "../../css/order_list/order_list.css";

const DEFAULT_IMAGE = "/assets/images/VaultAccess/no-image3.png";

const formatDateDisplay = (dateString, withTime = false) => {
    if (!dateString) return "-";
    try {
        const date = new Date(dateString);
        const options = { day: '2-digit', month: 'short', year: 'numeric' };
        if (withTime) { options.hour = '2-digit'; options.minute = '2-digit'; }
        return date.toLocaleDateString('en-IN', options);
    } catch (e) { return dateString; }
};

const formatPrice = (price) => {
    let numericPrice = 0;
    if (typeof price === "number") numericPrice = price;
    else if (typeof price === "string") numericPrice = parseFloat(price.replace(/[^\d.]/g, ""));
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(numericPrice || 0).replace('₹', 'Rs. ');
};

const OrderSkeleton = () => (
    <div className="order-col col-xxl-2 col-xl-2 col-lg-3 col-md-4 col-sm-6 col-12">
        <div className="order-card-compact">
            <div className="order-card-header">
                <Skeleton width="100px" height="1.2rem" />
                <Skeleton width="60px" height="1.2rem" borderRadius="20px" />
            </div>
            <div className="order-card-body">
                <Skeleton width="130px" height="0.8rem" style={{ marginBottom: '1rem' }} />
                <div className="card-label-row"><Skeleton width="40%" height="0.8rem" /></div>
                <div className="card-label-row" style={{ marginTop: '0.8rem' }}><strong><Skeleton width="100%" height="1.5rem" /></strong></div>
            </div>
            <div className="order-card-footer"><Skeleton width="100%" height="44px" /></div>
        </div>
    </div>
);

const DetailSkeleton = () => (
    <div className="order-layout">
        <div className="order-main-section">
            <div className="order-items-list">
                {[1, 2].map(i => (
                    <div className="order-item-card skeleton-item" key={i}>
                        <div className="item-left-visual">
                            <Skeleton width="180px" height="180px" borderRadius="12px" />
                        </div>
                        <div className="item-details-container">
                            <div className="item-info-col">
                                <Skeleton width="90%" height="1.8rem" style={{ marginBottom: '1.2rem' }} />
                                <div className="item-ref-code"><Skeleton width="60%" height="0.9rem" /></div>
                                <div className="item-ref-code" style={{ marginTop: '0.8rem' }}><Skeleton width="55%" height="0.9rem" /></div>
                                <div className="item-clock-date" style={{ marginTop: '1.5rem' }}>
                                    <Skeleton width="140px" height="0.8rem" />
                                </div>
                            </div>
                            <div className="spec-column">
                                <div className="spec-title-exact"><Skeleton width="80%" height="1.1rem" /></div>
                                <div className="spec-row-exact"><Skeleton width="100%" height="0.8rem" /></div>
                                <div className="spec-row-exact"><Skeleton width="100%" height="0.8rem" /></div>
                            </div>
                            <div className="spec-column">
                                <div className="spec-title-exact"><Skeleton width="80%" height="1.1rem" /></div>
                                <div className="spec-row-exact"><Skeleton width="100%" height="0.8rem" /></div>
                                <div className="spec-row-exact"><Skeleton width="100%" height="0.8rem" /></div>
                            </div>
                            <div className="order-item-actions">
                                <div className="item-price-exact"><Skeleton width="120px" height="2rem" /></div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
        <aside className="order-summary-sidebar">
            <Skeleton width="100%" height="150px" />
            <Skeleton width="100%" height="200px" style={{ marginTop: '2rem' }} />
        </aside>
    </div>
);

const OrderCard = ({ item, index, onViewDetail }) => {
    const s = (item.Status || item.STATUS || "").toLowerCase();
    let statusClass = "status-default";
    if (s.includes("pending")) statusClass = "status-pending";
    else if (s.includes("confirm")) statusClass = "status-confirmed";
    else if (s.includes("cancel")) statusClass = "status-cancelled";
    else if (s.includes("process")) statusClass = "status-processing";

    return (
        <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: index * 0.03 }}
            className="order-col col-xxl-2 col-xl-2 col-lg-3 col-md-4 col-sm-6 col-12"
        >
            <div className="order-card-compact">
                <div className="order-card-header">
                    <h3 className="order-no-compact">{(item.EnquiryNo)}</h3>
                    <span className={`status-badge-compact ${statusClass}`}>
                        {item.Status || "Pending"}
                    </span>
                </div>
                <div className="order-card-body">
                    <div className="order-date-compact">
                        {formatDateDisplay(item.EnquiryDate || item.ENQUIRYDATE || item.EntryDate || item.ENTRYDATE)}
                    </div>
                    <div className="card-label-row">
                        <strong>T.Style.:</strong> {(item.StyleCount || item.STYLECOUNT) || "0"}
                    </div>
                    <div className="card-label-row">
                        <strong>Total Amount:</strong> <span style={{ color: 'var(--text-primary)', fontWeight: '700' }}>{(item.LineAmount || item.LINEAMOUNT) || "Rs. 0"}</span>
                    </div>
                </div>
                <div className="order-card-footer">
                    <button className="view-detail-btn-full" onClick={() => onViewDetail(item)}>
                        View Order Detail
                    </button>
                </div>
            </div>
        </motion.div>
    );
};

const OrderDetailItem = ({ item, index }) => (
    <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: index * 0.1 }}
        className="order-item-card"
    >
        <div className="item-left-visual">
            <Link to={`/detail?style_ID=${item.Style_ID}`} style={{ display: 'block', height: '100%' }}>
                <img
                    src={item.Image1 || item.IMAGE1 || DEFAULT_IMAGE}
                    alt={item.StyleNo || item.STYLENO}
                    onError={(e) => e.target.src = DEFAULT_IMAGE}
                />
            </Link>
        </div>
        <div className="item-details-container">
            {/* Column 1: Info */}
            <div className="item-info-col">
                <h2 className="item-title-exact">
                    {item.StyleNo} - {item.Shortdescription || "Jewellery Item"}
                </h2>
                <div className="item-ref-code">
                    Jewel Code: <span>{item.JewelCode}</span>
                </div>
                <div className="item-ref-code">
                    Style No: <span>{item.StyleNo}</span>
                </div>
                <div className="item-clock-date">
                    <i className="pi pi-clock" style={{ fontSize: '0.9rem' }} />
                    {formatDateDisplay(item.EnquiryDate || item.ENQUIRYDATE, true)}
                </div>
            </div>

            {/* Column 2: Product Details */}
            <div className="spec-column">
                <div className="spec-title-exact">Product Details</div>
                <div className="spec-row-exact">
                    <span className="spec-label-exact">Purity</span>
                    <span className="spec-value-exact">{item.MetalQuality || item.METALQUALITY || "-"}</span>
                </div>
                <div className="spec-row-exact">
                    <span className="spec-label-exact">Gold Wt</span>
                    <span className="spec-value-exact">{item.TotalNetWt || item.TOTALNETWT || "0"} g</span>
                </div>
                <div className="spec-row-exact">
                    <span className="spec-label-exact">Dimension</span>
                    <span className="spec-value-exact">{item.ItemSize || item.ITEMSIZE || "-"}</span>
                </div>
            </div>

            {/* Column 3: Diamond Specs */}
            <div className="spec-column">
                <div className="spec-title-exact">Diamond Details</div>
                <div className="spec-row-exact">
                    <span className="spec-label-exact">Weight</span>
                    <span className="spec-value-exact">{item.DiaPcs || 0} Pcs / {item.DiaWt || 0} Cts</span>
                </div>
                <div className="spec-row-exact">
                    <span className="spec-label-exact">Gross Wt</span>
                    <span className="spec-value-exact">{item.GrossWt || 0} g</span>
                </div>
                <div className="spec-row-exact">
                    <span className="spec-label-exact">Color</span>
                    <span className="spec-value-exact">{item.MetalTone || "-"}</span>
                </div>
            </div>

            {/* Column 4: Price & Qty */}
            <div className="order-item-actions">
                <div className="item-price-exact">
                    {item.LineAmount || item.LINEAMOUNT || "Rs. 0"}
                </div>
                <div className="item-ref-code mt-auto">
                    Qty: <span>{item.Qty || item.QTY || "1"}</span>
                </div>
            </div>
        </div>
    </motion.div>
);

const OrderList = () => {
    document.title = "Orders | Meera Jewellery House Admin";
    const navigate = useNavigate();
    const { user, showToast } = useAuth();
    const [orderData, setOrderData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [dataFetched, setDataFetched] = useState(false);
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);
    const [searchOrderNo, setSearchOrderNo] = useState("");
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [enquiryItems, setEnquiryItems] = useState([]);
    const [isDetailLoading, setIsDetailLoading] = useState(false);

    const { subtotal, total } = useMemo(() => {
        let sub = 0;
        enquiryItems.forEach(item => {
            let price = item.TotalAmount || item.TOTALAMOUNT || item.Totalamount;
            if (price === undefined || price === null) {
                const priceStr = String(item.LineAmount || item.LINEAMOUNT || "0");
                price = parseFloat(priceStr.replace(/[^0-9.]/g, ""));
            }
            sub += isNaN(price) ? 0 : Number(price);
        });
        return { subtotal: sub, total: sub };
    }, [enquiryItems]);

    const fetchOrders = useCallback(async (currentFilters = {}) => {
        try {
            setIsLoading(true);
            const formatDateForApi = (date) => {
                if (!date) return "";
                const d = new Date(date);
                return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}/${d.getFullYear()}`;
            };

            const response = await GetMyOrderList({
                Party_ID: user?.SelectedCustomer_ID || "0",
                EnquirySearch: currentFilters.search || "",
                FromDate: formatDateForApi(currentFilters.from),
                ToDate: formatDateForApi(currentFilters.to),
                PageNo: "1", PageSize: "50"
            });
            setOrderData(response?.Row || response?.ROW || []);
            setDataFetched(true);
        } catch (error) {
            console.error("Fetch Orders Error:", error);
        } finally {
            setIsLoading(false);
        }
    }, [user?.SelectedCustomer_ID]);

    useEffect(() => {
        const timer = setTimeout(() => fetchOrders({ from: fromDate, to: toDate, search: searchOrderNo }), 500);
        return () => clearTimeout(timer);
    }, [fromDate, toDate, searchOrderNo, fetchOrders]);

    const handleViewDetail = async (order) => {
        setSelectedOrder(order);
        setIsDetailLoading(true);
        setEnquiryItems([]);
        try {
            const response = await GetMyOrderDetail({
                Enquiry_ID: order.Enquiry_ID || order.ENQUIRY_ID,
                Party_ID: user?.SelectedCustomer_ID || "0",
                OrderCategory: "ORDER"
            });
            setEnquiryItems(response?.ROW || response?.Row || response?.EnquiryDetail || []);
        } catch (error) {
            console.error("Detail Error:", error);
            showToast({ title: "Error", message: "Failed to load order details", type: "error" });
        } finally {
            setIsDetailLoading(false);
        }
    };

    const selectedCustomer = user?.customerList?.find(c => String(c.Customer_ID) === String(user.SelectedCustomer_ID));
    const firstItem = enquiryItems[0] || {};

    return (
        <div className="order-list-page">
            <div className="order-header-card">
                <div className="order-header-top-row">
                    <h1 className="order-page-title">{selectedOrder ? "Order Details" : "Orders"} <span>({selectedOrder ? enquiryItems.length : orderData.length})</span></h1>
                    <div className="order-header-actions-right" style={{ gap: '15px' }}>
                        {!selectedOrder && (
                            <div className="order-filters-compact">
                                <div className="compact-filter-item"><div className="compact-input-wrapper"><i className="pi pi-calendar" /><Calendar value={fromDate} onChange={(e) => setFromDate(e.value)} placeholder="From Date" showIcon={false} dateFormat="dd/mm/yy" /></div></div>
                                <div className="compact-filter-item"><div className="compact-input-wrapper"><i className="pi pi-calendar" /><Calendar value={toDate} onChange={(e) => setToDate(e.value)} placeholder="To Date" showIcon={false} dateFormat="dd/mm/yy" /></div></div>
                                <div className="compact-filter-item"><div className="compact-input-wrapper"><i className="pi pi-search" /><InputText value={searchOrderNo} onChange={(e) => setSearchOrderNo(e.target.value)} placeholder="OR/00/00" /></div></div>
                            </div>
                        )}
                        <div className="header-actions-group">
                            <div className="order-header-actions-left"><CustomerSelection /></div>
                            <button className="back-btn" onClick={() => selectedOrder ? setSelectedOrder(null) : navigate(-1)} aria-label="Go back">
                                <i className="pi pi-arrow-left" />
                            </button>
                        </div>
                    </div>
                </div>
                <AnimatePresence>
                    {selectedOrder && (
                        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="detail-back-container mt-2">
                            <div className="order_summary_details">
                                <button className="back-to-orders-link" onClick={() => setSelectedOrder(null)}>
                                    <i className="pi pi-arrow-left" style={{ fontSize: '0.8rem' }} /> Back to Orders
                                </button>
                                <span className="divider" />
                                <span className="order_placed_date">Order placed on {formatDateDisplay(selectedOrder.EnquiryDate)}</span>
                                <span className="divider" />
                                <span className="order_number">Order number: {selectedOrder.EnquiryNo}</span>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>


            <div className="order-content">
                <AnimatePresence mode='wait'>
                    {selectedOrder ? (
                        <div key="detail-view">
                            {isDetailLoading ? <DetailSkeleton /> : (
                                <div className="order-layout">
                                    <div className="order-main-section">
                                        <div className="order-items-list">
                                            {enquiryItems.map((item, idx) => <OrderDetailItem key={idx} item={item} index={idx} />)}
                                        </div>
                                    </div>
                                    <aside className="order-summary-sidebar">
                                        <h2 className="order-summary-title">Shipping Address</h2>
                                        <div className="sidebar-block">
                                            <div className="address-text">
                                                {firstItem.ShippingAddress1 ? (
                                                    <>{firstItem.ShippingAddress1}<br />{firstItem.ShippingAddress2 && <>{firstItem.ShippingAddress2}<br /></>}{firstItem.ShippingCity}, {firstItem.ShippingState} {firstItem.ShippingZipCode}<br />{firstItem.ShippingCountryName || "India"}</>
                                                ) : selectedCustomer ? (
                                                    <>{selectedCustomer.Address || "Address not provided"}<br />{selectedCustomer.City}, {selectedCustomer.State}<br />{selectedCustomer.Country || "India"}</>
                                                ) : "No address available"}
                                            </div>
                                        </div>
                                        <h2 className="order-summary-title mt-3">Order Summary</h2>
                                        <div className="summary-row"><span className="spec-label-exact">Subtotal</span><span className="spec-value-exact">{formatPrice(subtotal)}</span></div>
                                        <div className="summary-row"><span className="spec-label-exact">Shipping</span><span className="spec-value-exact">Free</span></div>
                                        <div className="summary-row total">
                                            <span className="spec-label-exact">Total Amount</span>
                                            <span>{formatPrice(total)}</span>
                                        </div>
                                    </aside>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="order-content-scrollable" key="list-view">
                            {isLoading && !dataFetched ? (
                                <div className="order-grid-row row g-3">
                                    {Array.from({ length: 12 }).map((_, i) => <OrderSkeleton key={i} />)}
                                </div>
                            ) : (
                                dataFetched && orderData.length === 0 ? (
                                    <div className="empty-cart-premium">
                                        <div className="empty-cart-visual">
                                            <i className="pi pi-receipt" />
                                        </div>
                                        <div className="empty-cart-content">
                                            <h2 className="empty-cart-title">No Orders Found</h2>
                                            <p className="empty-cart-text">
                                                It looks like you haven't placed any orders yet.
                                                Explore our collections to find your perfect match.
                                            </p>
                                            <Link to="/StyleInventory" className="btn-primary">
                                                <i className="pi pi-compass me-2" />
                                                Browse Collections
                                            </Link>
                                        </div>
                                    </div>
                                ) : (
                                    <motion.div key={`${fromDate}-${toDate}-${searchOrderNo}`} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }} className="order-grid-row row g-3">
                                        {orderData.map((item, index) => <OrderCard key={item.Enquiry_ID || index} item={item} index={index} onViewDetail={handleViewDetail} />)}
                                    </motion.div>
                                )
                            )}
                        </div>
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
};

export default OrderList;
